import { Component, OnInit } from '@angular/core';
import { Trip } from '../../../models/trips/trip';
import { TripService } from '../../../services/trip.service';
import { ActivatedRoute, Router } from '@angular/router';
import { AlertService } from '../../../services/alert.service';
import { Form, FormGroup, FormControl, Validators, FormArray } from '@angular/forms';
import { KeyValuePair } from '../../../models/common/keyValuePair';
import { TripContainer } from '../../../models/trips/tripContainer';
import { TableColumn } from '../../../models/tables/TableColumn';
import { RequestResult } from '../../../models/common/requestResult';
import { CustomValidators } from '../../../validators/customValidators';
import { APP_ROUTES } from '../../../contants/APP_ROUTES';

@Component({
	selector: 'app-trip-form',
	templateUrl: './trip-form.component.html',
	styleUrls: ['./trip-form.component.css']
})
export class TripFormComponent implements OnInit {

	form: FormGroup;
	trip: Trip;

	constructor(
		private _tripService: TripService,
		private _activatedRoute: ActivatedRoute,
		private _router: Router,
		private _alertService: AlertService
	) { }

	ngOnInit() {
		let id = this._activatedRoute.snapshot.params['id'];
		this.trip = new Trip();
		this.trip.id = id;

		this.form = this.createForm(this.trip);

		if (id == 0) {
			this.trip.id = 0;
		}
		else {
			this._tripService.getTrip(id).subscribe(
				trip => {
					if (trip == undefined) {
						this.trip.id = 0;
					}
					else {
						this.trip = trip;
						this.reloadForm();
					}
				}
			);
		}
	}

	private reloadForm(){
		this.form = this.createForm(this.trip);
		if(this.trip.tripContainers !== undefined){
			this.trip.tripContainers.forEach(x => this.addContainer(x));
		}
	}

	private createForm(trip: Trip): FormGroup {
		return new FormGroup({
			billOfLading: new FormControl(trip.billOfLading, [
				Validators.required,
				Validators.maxLength(30)
			]),
			clientId: new FormControl(trip.clientId, [
				Validators.required
			]),
			shippingLineId: new FormControl(trip.shippingLineId, [
				Validators.required
			]),
			tripContainers: new FormArray([]),
			tripTypeId: new FormControl(trip.tripTypeId, [
				Validators.required
			]),
		});
	}

	private createTripContainerForm(source?: TripContainer): FormGroup {
		let isDisabled = false;
		if(source == null || source == undefined){
			source = new TripContainer();
		}
		else{
			isDisabled = source.status == 'DONE' || source.status == 'IN_PROGRESS' ? true : false;
		}		
		
		let formGroup = new FormGroup({
			id: new FormControl(source.id),
			status: new FormControl(source.status),
			containerNumber: new FormControl({value: source.containerNumber, disabled: isDisabled}, [
				Validators.required,
				Validators.maxLength(11)
			]),
			containerNumberTwo: new FormControl({value: source.containerNumberTwo, disabled: isDisabled}),
			containerSize: new FormControl({value: source.containerSize, disabled: isDisabled}, [
				Validators.required,
				Validators.maxLength(15)
			]),
			pickupPoint: new FormControl({value: source.pickupPoint, disabled: isDisabled}, [
				Validators.required,
				Validators.maxLength(11)
			]),
			destination: new FormControl({value: source.destination, disabled: isDisabled}, [
				Validators.required,
				Validators.maxLength(50)
			]),
			tripRate: new FormControl(source.tripRate, [
				Validators.required,
				Validators.maxLength(9),
				CustomValidators.validateCurrency
			]),
			driverRate: new FormControl(source.driverRate, [
				Validators.required,
				Validators.maxLength(9),
				CustomValidators.validateCurrency
			]),
			helperRate: new FormControl(source.helperRate, [
				Validators.required,
				Validators.maxLength(9),
				CustomValidators.validateCurrency
			]),
			dailyTripReturnEmptyDate: new FormControl(source.dailyTripReturnEmptyDate, null)
		});

		formGroup.get('containerSize').valueChanges.subscribe(
			size => {
				var containerNumberTwo = formGroup.get('containerNumberTwo');
				if(size.indexOf('2x') != -1){
					containerNumberTwo.setValidators([Validators.required, Validators.maxLength(11)]);
					containerNumberTwo.enable();
				}
				else{
					containerNumberTwo.clearValidators();
					containerNumberTwo.disable();
					containerNumberTwo.setValue('');
				}

				containerNumberTwo.updateValueAndValidity();
			}
		);

		return formGroup;
	}

	onFormSubmit(formValue) {
		//update trip item
		if (!this.form.valid) {
			this._alertService.showError("Form is invalid, all inputs marked with '*' are required.");
			return;
		}

		this.trip.billOfLading = formValue.billOfLading;
		this.trip.clientId = formValue.clientId;
		this.trip.shippingLineId = formValue.shippingLineId;
		this.trip.tripTypeId = Number(formValue.tripTypeId);
		//update containers
		this.trip.tripContainers = [];
		
		let rawValue = this.form.getRawValue();
		let formArr = (this.form.controls.tripContainers) as FormArray;
		for(let index = 0; index < formArr.length; index++){
			if(formArr.controls[index].get('containerNumber').disabled){
				//get raw value
				this.trip.tripContainers.push(rawValue.tripContainers[index]);
			}
			else{
				const container = formValue.tripContainers[index];
				if(container.containerSize.indexOf('2x') == -1){
					if(container.containerNumberTwo != null && container.containerNumberTwo != undefined && container.containerNumberTwo != '') {
						this._alertService.showError(`Form is invalid, only one container is allowed for ${container.containerSize}.`);
						return;
					}
				}

				this.trip.tripContainers.push(container);
			}
		}		

		if (this.trip.id == undefined || this.trip.id == 0) {
			this.createTrip();
		}
		else {
			this.updateTrip();
		}
	}

	private createTrip() {
		this._tripService.addTrip(this.trip).subscribe(
			result => {
				let newTrip = result.object;
				if (newTrip != null) {
					this.trip = newTrip;
					this._alertService.showSuccess('Success');
					this.reloadForm();
				}
				else {
					this._alertService.displayErrorResult(result.errorCollection);
				}
			});
	}

	private updateTrip() {
		this._tripService.updateTrip(this.trip).subscribe(
			result => {
				let updatedTrip = result.object;
				if (updatedTrip != null) {
					this.trip = updatedTrip;
					this._alertService.showSuccess('Success');
					this.reloadForm();
				}
				else {
					this._alertService.displayErrorResult(result.errorCollection);
				}
			}
		);
	}

	isControlInvalid(controlName: string) {
		let control = this.form.controls[controlName];
		if(control == undefined){
			return false
		}
		return control.invalid && (control.dirty || control.touched);
	}

	addContainer(source?: TripContainer){
		if(source === undefined || source === null){
			source = new TripContainer();
		}

		let containers = this.form.get('tripContainers') as FormArray;
		containers.push(this.createTripContainerForm(source));
	}

	onAddContainerClick(event) {
		this.addContainer();
	}

	onRemoveContainerClick(index: number) {
		let containers = this.form.get('tripContainers') as FormArray;
		containers.removeAt(index);
	}

	allowEditForContainer(index: number){
		let status = this.getContainerStatus(index);
		if(status == 'DONE' || status == 'IN_PROGRESS') {
			return false;
		}

		return true;
	}

	getContainerStatus(index: number){
		return this.form.value.tripContainers[index].status;
	}

	getContainerStatusLabel(index: number){
		const status = this.form.value.tripContainers[index].status;
		const returnEmptyDate = this.form.value.tripContainers[index].dailyTripReturnEmptyDate;
		return TripContainer.getStatusDisplayName(this.form.value.tripTypeId, status, returnEmptyDate);
	}

	viewDailyTrips(){
		this._router.navigate([APP_ROUTES.dailyTripList], {queryParams: {
			isDateNow: false,
			tripId: this.trip.id
		}});
	}

	duplicateContainerDetailsClick(index: number) {
		let containers = this.form.get('tripContainers') as FormArray;
		let toDuplicate = containers.value[index];
		
		let newContainer = new TripContainer();
		newContainer.containerSize = toDuplicate.containerSize;
		newContainer.pickupPoint = toDuplicate.pickupPoint;
		newContainer.destination = toDuplicate.destination;
		newContainer.tripRate = toDuplicate.tripRate;
		newContainer.driverRate = toDuplicate.driverRate;
		newContainer.helperRate = toDuplicate.helperRate;

		this.addContainer(newContainer);
    }
    
    deleteTrip(){
        this._tripService.deleteTrip(this.trip.id).subscribe(
			result => {
				if (result.object != null) {
					this.trip = result.object;
					this._alertService.showSuccess('Trip is deleted');
					this.reloadForm();
				}
				else {
					this._alertService.displayErrorResult(result.errorCollection);
				}
			});
    }
}



// WEBPACK FOOTER //
// ./src/app/components/trips/trip-form/trip-form.component.ts